import { db } from './db.js';
import { baseRecord } from './id.js';
export class CoughDayConflictError extends Error {
    rows;
    constructor(rows) {
        super('같은 날짜의 기침 기록이 여러 개예요. 원본을 비교한 뒤 사용할 기록을 골라 주세요.');
        this.rows = rows;
    }
}
const observed = (row, field) => row[field === 'dayCount' ? 'dayObserved' : 'nightObserved'] ?? row[field] > 0;
async function dayRows(petId, date) { return (await db.coughLogs.where('[petId+date]').equals([petId, date]).toArray()).filter((row) => !row.deletedAt && !row.duplicateOf); }
/** One IDB transaction reads the current count. Retries reuse an operation ID. */
export async function recordCoughObservation(input) {
    return db.transaction('rw', db.coughLogs, async () => {
        const rows = await dayRows(input.petId, input.date);
        if (rows.length > 1)
            throw new CoughDayConflictError(rows);
        const current = rows[0] ?? baseRecord({ petId: input.petId, date: input.date, dayCount: 0, nightCount: 0, dayObserved: false, nightObserved: false });
        const row = current;
        if (row.operations?.some((operation) => operation.id === input.operationId))
            return row;
        const operation = { ...(input.actorLabel ? { actorLabel: input.actorLabel } : {}), id: input.operationId, at: Date.now(), field: input.field, delta: input.kind === 'increment' ? 1 : 0, observedBefore: observed(row, input.field) };
        const next = { ...row, [input.field]: row[input.field] + operation.delta, [input.field === 'dayCount' ? 'dayObserved' : 'nightObserved']: true,
            attachmentIds: [...new Set([...(row.attachmentIds ?? []), ...(input.attachmentIds ?? [])])], operations: [...(row.operations ?? []), operation], updatedAt: Math.max(Date.now(), row.updatedAt + 1) };
        await db.coughLogs.put(next);
        return next;
    });
}
export async function undoCoughObservation(petId, date, operationId) {
    return db.transaction('rw', db.coughLogs, async () => {
        const rows = await dayRows(petId, date);
        if (rows.length !== 1)
            throw new CoughDayConflictError(rows);
        const row = rows[0];
        const operation = row.operations?.find((item) => item.id === operationId);
        if (!operation || operation.undoneAt)
            return row;
        if (row[operation.field] < operation.delta)
            throw new Error('횟수가 따로 수정되어 이 동작을 되돌릴 수 없어요. 현재 기록을 확인해 주세요.');
        const count = row[operation.field] - operation.delta;
        const otherObservation = row.operations.some((item) => item.id !== operationId && item.field === operation.field && !item.undoneAt);
        const next = { ...row, [operation.field]: count, [operation.field === 'dayCount' ? 'dayObserved' : 'nightObserved']: (row.operations.find((item) => item.field === operation.field)?.observedBefore ?? false) || otherObservation || count > 0,
            operations: row.operations.map((item) => item.id === operationId ? { ...item, undoneAt: Date.now() } : item), updatedAt: Math.max(Date.now(), row.updatedAt + 1) };
        await db.coughLogs.put(next);
        return next;
    });
}
/** Explicit choice preserves every unselected original; never adds uncertain counts. */
export async function resolveCoughDay(petId, date, selectedId, expected) {
    return db.transaction('rw', db.coughLogs, async () => {
        const rows = await dayRows(petId, date);
        if (rows.length !== expected.length || rows.some((row) => !expected.some((item) => item.id === row.id && item.updatedAt === row.updatedAt)))
            throw new CoughDayConflictError(rows);
        const selected = rows.find((row) => row.id === selectedId);
        if (!selected)
            throw new Error('사용할 기록을 다시 골라 주세요.');
        for (const row of rows)
            if (row.id !== selectedId)
                await db.coughLogs.put({ ...row, duplicateOf: selectedId, updatedAt: Math.max(Date.now(), row.updatedAt + 1) });
        return selected;
    });
}
